<template>
  <div class="question">
    <i
      class="remove ion ion-trash-b text-muted"
      @click.prevent="$emit('remove-clicked')"
    ></i>
    <div class="question-header">
      <i class="handle ion ion-drag text-muted"></i>
      <span># {{ idx + 1 }} - {{ question.type | questionTypeLabel }}</span>
      <span v-if="question.profile_attr"
        >({{ question.profile_attr | profileAttrLabel }})</span
      >
    </div>

    <div class="question-content">
      <div class="row">
        <div class="col-sm-9">
          <textarea
            v-model="questionObj.text"
            v-autosize
            class="form-control overview-input question-input margin-top-20"
            placeholder="Question"
            rows="1"
            :disabled="question.profile_attr"
          ></textarea>
          <p
            v-for="(error, errorIdx) in questionErrors"
            :key="errorIdx"
            class="text-danger"
          >
            {{ error }}
          </p>
        </div>

        <div class="col-sm-3">
          <div class="checkbox question-checkbox">
            <label>
              <input v-model="questionObj.required" type="checkbox" />
              <span>Required</span>
            </label>
          </div>
        </div>
      </div>

      <div v-if="isChoicesType(question.type)">
        <hr class="separator" />

        <div v-if="question.widget === 'DROPDOWN'">
          <MultipleChoicesDropdown
            title="option"
            :choices="question.choices"
            :single-choice="question.type === 'SINGLE_CHOICES'"
            :show-clear-all="
              question.type ===
              'MULTIPLE_CHOICES && question.choices.length >= 5'
            "
            :search-enabled="question.choices.length > 8"
          />
        </div>

        <div v-if="question.widget !== 'DROPDOWN'">
          <draggable
            v-model="questionObj.choices"
            :options="{ handle: '.handle' }"
            class="choices"
          >
            <div
              v-for="(choice, choiceIndex) in question.choices"
              :key="choiceIndex"
              class="choice"
            >
              <i
                v-if="canChangeChoicesOrder"
                class="handle ion ion-drag text-muted"
              ></i>
              <input
                v-model="choice.value"
                class="form-control choice-input"
                placeholder="Type in a choice"
                :disabled="question.profile_attr"
                @keyup.enter="addChoice(question)"
              />
              <i
                v-if="canRemoveChoices"
                class="remove-choice ion ion-close"
                @click="removeChoice(question, choiceIndex)"
              ></i>
              <hr class="separator" />
            </div>
            <span
              v-if="canAddChoices"
              class="add-choice text-muted"
              @click="addChoice(question)"
              >Add choice</span
            >
          </draggable>
        </div>
      </div>

      <div v-if="question.type === 'AGREEMENT'">
        <hr class="separator" />
        <input
          v-model="questionObj.url"
          class="form-control"
          placeholder="Paste link to agreement document"
        />
      </div>

      <div v-if="question.type === 'IMAGE'">
        <hr class="separator" />
        <Dropzone title="Upload logo" @upload-done="onImageUploaded" />
        <img v-if="imageUrl" class="image-answer" :src="imageUrl" />
      </div>

      <div v-if="question.type === 'ATTACHMENT'">
        <hr class="separator" />
        <Dropzone title="Upload attachment" :enabled="false" />
      </div>
    </div>
  </div>
</template>

<script>
import autosize from '@/directives/autosize'
import draggable from 'vuedraggable'
import { questionTypeLabel } from '@/filters'
import Dropzone from '@/components/Dropzone'
import QUESTIONS from '@/modules/dashboard-organizer/CompanyProfileQuestions'

export default {
  directives: { autosize },
  filters: {
    questionTypeLabel,
    profileAttrLabel(attr) {
      return QUESTIONS[attr].label
    },
  },
  components: { draggable, Dropzone },
  props: {
    question: {
      type: Object,
      default: () => ({}),
    },
    idx: {
      type: Number,
      default: null,
    },
    questionErrors: {
      type: Array,
      default: () => [],
    },
    enabled: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    imageUrl: null,
  }),
  computed: {
    canAddChoices() {
      return !this.question.profile_attr
    },
    canChangeChoicesOrder() {
      return !this.question.profile_attr
    },
    canRemoveChoices() {
      return !this.question.profile_attr
    },
    questionObj: {
      get() {
        return this.question
      },
      set(value) {
        this.$emit('question-updated', { idx: this.idx, value })
      },
    },
  },
  methods: {
    isChoicesType(type) {
      const choicesTypes = new Set(['SINGLE_CHOICES', 'MULTIPLE_CHOICES'])
      return choicesTypes.has(type)
    },

    addChoice(question) {
      const self = this
      self.questionObj.choices.push({ label: '', value: '' })

      setTimeout(function () {
        _.last(self.$el.querySelectorAll('.choice-input')).focus()
      }, 25)
    },

    removeChoice(question, choiceIdx) {
      question.choices = question.choices.filter((c, idx) => idx !== choiceIdx)
    },

    onImageUploaded({ file, url }) {
      this.imageUrl = url
    },
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';

.program-editor {
  $question-color: $grey120;

  .question {
    border: 2px solid $grey150;
    border-radius: 3px;
    position: relative;
    margin-bottom: 20px;
    color: $question-color;

    .question-header {
      padding: 3px 5px;
      background-color: $grey180;
      color: $grey90;
    }

    .question-content {
      padding: 3px 5px;
      background-color: $white;
    }

    &.sortable-chosen {
      opacity: 1;
    }

    &.sortable-ghost {
      background-color: $grey180;
      opacity: 1;

      &:after {
        width: 100%;
        height: 100%;
        background: $grey180;
        border-color: $grey180;
        content: ' ';
        position: absolute;
        top: 0;
        left: 0;
      }
    }

    .question-input {
      color: $question-color;
      // margin-left: 40px;
      border-bottom: none;
      resize: none;

      &[disabled] {
        background-color: $white;
      }
    }

    .separator {
      margin: 5px -5px 3px -5px;
    }

    .image-answer {
      max-width: 400px;
      max-height: 400px;
      text-align: center;
    }

    .choices {
      .choice {
        position: relative;

        .choice-input {
          border: none;
          margin-top: 0;
          padding: 0px 4px;
          color: $question-color;
          height: 28px;
          display: inline-block;
          width: 300px;

          &[disabled] {
            background-color: $white;
          }

          $placeholder-opacity: 0.5;
          &::-webkit-input-placeholder {
            /* WebKit, Blink, Edge */
            opacity: $placeholder-opacity;
          }

          &:-moz-placeholder {
            /* Mozilla Firefox 4 to 18 */
            opacity: $placeholder-opacity;
          }

          &::-moz-placeholder {
            /* Mozilla Firefox 19+ */
            opacity: $placeholder-opacity;
          }

          &:-ms-input-placeholder {
            /* Internet Explorer 10-11 */
            opacity: $placeholder-opacity;
          }

          &::-ms-input-placeholder {
            /* Microsoft Edge */
            opacity: $placeholder-opacity;
          }
        }

        .remove-choice {
          position: absolute;
          right: 10px;
          top: 5px;
          display: none;
          cursor: pointer;
          &:hover {
            color: $grey90;
          }
        }

        &:hover {
          .remove-choice {
            display: block;
          }
        }
      }

      .add-choice {
        padding-left: 10px;
        cursor: pointer;
        font-size: 15px;
        &:hover {
          color: $grey90;
        }
      }
    }

    .remove {
      position: absolute;
      right: 10px;
      top: 5px;
      cursor: pointer;
      &:hover {
        color: $grey90;
      }
    }

    .handle {
      // position: absolute;
      // right: 30px;
      // top: 5px;
      padding: 0 10px;
      cursor: pointer;
      &:hover {
        color: $grey90;
      }
    }
  }
}
</style>
